import React, { useState, useEffect, useCallback, useRef } from 'react';
import { authFetch, logout } from './authFetch';

// HIPAA-compliant idle timeout configuration
// Matches backend SESSION_COOKIE_AGE = 900 (15 minutes)
const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes in milliseconds
const WARNING_BEFORE_MS = 3 * 60 * 1000; // Show warning 3 minutes before timeout
const WARNING_THRESHOLD_MS = IDLE_TIMEOUT_MS - WARNING_BEFORE_MS; // 12 minutes

// Production mode check
const IS_DEV = process.env.NODE_ENV === 'development';
const devLog = (message) => { if (IS_DEV) console.log(`[IdleTimeout] ${message}`); };

// Play a tone using Web Audio API
const playTone = (frequency, beepCount, beepDuration = 150, gapDuration = 100) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    for (let i = 0; i < beepCount; i++) {
      const startTime = ctx.currentTime + i * (beepDuration + gapDuration) / 1000;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = frequency;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + beepDuration / 1000);
      osc.start(startTime);
      osc.stop(startTime + beepDuration / 1000);
    }
  } catch (e) {
    devLog('Audio tone failed: ' + e.message);
  }
};

const IdleTimeout = ({ children, isAuthenticated }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(WARNING_BEFORE_MS);
  const [isExtending, setIsExtending] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const lastActivityRef = useRef(Date.now());
  const warningIntervalRef = useRef(null);
  const checkIntervalRef = useRef(null);
  const showWarningRef = useRef(showWarning);
  const warningTonePlayedRef = useRef(false);
  const urgentTonePlayedRef = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    showWarningRef.current = showWarning;
  }, [showWarning]);

  // Reset the activity timer
  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);
    setRemainingTime(WARNING_BEFORE_MS);
    warningTonePlayedRef.current = false;
    urgentTonePlayedRef.current = false;
  }, []);

  // Handle user activity events - uses ref to avoid stale closure
  const handleActivity = useCallback(() => {
    if (!showWarningRef.current) {
      // Only reset on activity if warning is not showing
      // This prevents accidental resets when user is AFK but moves mouse slightly
      lastActivityRef.current = Date.now();
    }
  }, []);

  // Perform logout and cleanup
  const performLogout = useCallback(async () => {
    // Prevent multiple logout calls
    if (isLoggingOut) return;

    devLog('Session timeout - logging out');

    // Immediately hide the modal
    setIsLoggingOut(true);
    setShowWarning(false);

    // Clear any intervals
    if (warningIntervalRef.current) {
      clearInterval(warningIntervalRef.current);
    }
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
    }

    try {
      // Pass the redirect path with timeout error to the logout function
      await logout('/login?error=session_timeout');
    } catch (error) {
      devLog('Logout error during timeout');
    }
  }, [isLoggingOut]);

  // Extend session by making an API call
  const extendSession = useCallback(async () => {
    setIsExtending(true);
    devLog('Extending session');

    try {
      const BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const response = await authFetch(`${BASE_URL}/api/auth/user/`);

      if (response.ok) {
        devLog('Session extended successfully');
        resetTimer();
      } else {
        devLog('Failed to extend session - logging out');
        await performLogout();
      }
    } catch (error) {
      devLog('Error extending session');
      await performLogout();
    } finally {
      setIsExtending(false);
    }
  }, [resetTimer, performLogout]);

  // Check idle time and manage warning state
  useEffect(() => {
    if (!isAuthenticated) {
      // Clear intervals if not authenticated
      if (warningIntervalRef.current) {
        clearInterval(warningIntervalRef.current);
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      return;
    }

    // Check idle status every second
    checkIntervalRef.current = setInterval(() => {
      const idleTime = Date.now() - lastActivityRef.current;

      if (idleTime >= IDLE_TIMEOUT_MS) {
        // Timeout reached - logout
        performLogout();
      } else if (idleTime >= WARNING_THRESHOLD_MS) {
        // Show warning (setShowWarning handles deduplication internally)
        setShowWarning(prevShowWarning => {
          if (!prevShowWarning) {
            devLog('Showing idle warning');
            setRemainingTime(IDLE_TIMEOUT_MS - idleTime);
          }
          return true;
        });
      }
    }, 1000);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [isAuthenticated, performLogout]);

  // Play warning tone when modal first appears
  useEffect(() => {
    if (showWarning && !warningTonePlayedRef.current) {
      warningTonePlayedRef.current = true;
      playTone(800, 2); // gentle 800Hz double-beep
    }
  }, [showWarning]);

  // Play urgent tone at 1 minute remaining
  useEffect(() => {
    if (showWarning && remainingTime <= 60000 && remainingTime > 0 && !urgentTonePlayedRef.current) {
      urgentTonePlayedRef.current = true;
      playTone(1000, 3); // urgent 1000Hz triple-beep
    }
  }, [showWarning, remainingTime]);

  // Update countdown timer when warning is shown
  useEffect(() => {
    if (showWarning) {
      warningIntervalRef.current = setInterval(() => {
        const idleTime = Date.now() - lastActivityRef.current;
        const remaining = IDLE_TIMEOUT_MS - idleTime;

        if (remaining <= 0) {
          performLogout();
        } else {
          setRemainingTime(remaining);
        }
      }, 1000);

      return () => {
        if (warningIntervalRef.current) {
          clearInterval(warningIntervalRef.current);
        }
      };
    }
  }, [showWarning, performLogout]);

  // Set up activity listeners - only runs once when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    // Throttle activity handler to prevent excessive calls
    let throttleTimeout = null;
    const throttledHandler = () => {
      if (throttleTimeout) return;
      throttleTimeout = setTimeout(() => {
        throttleTimeout = null;
        handleActivity();
      }, 1000); // Throttle to once per second
    };

    events.forEach(event => {
      document.addEventListener(event, throttledHandler, { passive: true });
    });

    // Reset timer on initial mount only
    lastActivityRef.current = Date.now();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledHandler);
      });
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [isAuthenticated, handleActivity]);

  // Format remaining time for display
  const formatTime = (ms) => {
    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {children}

      {/* Warning Modal */}
      {showWarning && isAuthenticated && !isLoggingOut && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="timeout-title"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-yellow-500 px-6 py-4">
              <h2 id="timeout-title" className="text-xl font-bold text-white flex items-center">
                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Session Expiring Soon
              </h2>
            </div>

            {/* Body */}
            <div className="px-6 py-6">
              <p className="text-gray-700 mb-4">
                Your session will expire due to inactivity in:
              </p>

              <div className="text-center mb-6">
                <span className="text-4xl font-bold text-red-600 font-mono">
                  {formatTime(remainingTime)}
                </span>
              </div>

              <p className="text-gray-600 text-sm mb-6">
                For your security and HIPAA compliance, inactive sessions are automatically logged out after 15 minutes.
              </p>

              {/* Actions */}
              <div className="flex space-x-3">
                <button
                  onClick={extendSession}
                  disabled={isExtending}
                  className={`flex-1 py-3 px-4 rounded font-medium text-white transition-colors ${
                    isExtending
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {isExtending ? 'Extending...' : 'Stay Logged In'}
                </button>

                <button
                  onClick={performLogout}
                  className="flex-1 py-3 px-4 rounded font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors"
                >
                  Log Out Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default IdleTimeout;
